import { useHomeCrumb } from "@/components/PageBreadcrumbs";
import {
  cancelSnackOrder,
  getSnackOrderOptions,
  snackMenuItemsOptions,
  snackSupplierOptions,
  useBuilding,
  useSnackOrder,
} from "@/api";
import PageBackButton from "@/components/PageBackButton";
import { useBuildingQuery } from "@/components/RouterSetup";
import ContactDetails from "@/components/snack/ContactDetails";
import SnackOrderFormCheckout from "@/components/snack/order_form/Checkout";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import { getBuildingId, getApiErrorDetails } from "@/lib";
import { CustomerOrderStatus } from "@/components/snack/CustomerOrderStatus";
import { OrderReadError } from "@/components/snack/OrderReadError";
import {
  useLoggedInUser,
  useRouterStore,
  useSnackbarStore,
} from "@/store/zustandStore";
import { NotificationType, SnackOrderStatus, type SnackOrder } from "@/types";
import {
  Breadcrumbs,
  Button,
  LoadingSpinner,
  StatusText,
} from "@app-components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_portal/orders/$orderId")({
  component: RouteComponent,
  loader: ({ params }) => {
    const { queryClient, supplierCompanyId, user } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(
      getSnackOrderOptions(
        params.orderId,
        user.buildingTenantId,
        supplierCompanyId,
        user.userId,
      ),
    );
    queryClient.prefetchQuery(snackMenuItemsOptions(supplierCompanyId));
    queryClient.prefetchQuery(snackSupplierOptions(supplierCompanyId));
  },
});

function RouteComponent() {
  const { hasWelcomeSnack } = useWelcomeSnackAccess(false, true);

  if (!hasWelcomeSnack) return;

  return (
    <Suspense
      fallback={
        <div className="center">
          <LoadingSpinner />
        </div>
      }
    >
      <OrderDetails />
    </Suspense>
  );
}

function OrderDetails() {
  const { t } = useTranslation();
  const { orderId } = Route.useParams();
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();
  const currUser = useLoggedInUser();
  const { data: building } = useBuilding();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const {
    data: order,
    error,
    isPending,
    isFetching,
    refetch,
  } = useSnackOrder(
    orderId,
    currUser.buildingTenantId,
    building?.supplierCompanyId,
    currUser.userId,
  );
  const navigate = useNavigate();
  const readStatus = getApiErrorDetails(error).status;
  useEffect(() => {
    if (readStatus === 404) navigate({ to: "/404", replace: true });
  }, [readStatus, navigate]);
  const homeCrumb = useHomeCrumb();
  const breadcrumbs = [
    ...homeCrumb,
    { label: t("PAGES.MY_ORDERS.HEADER"), path: "/orders" },
    { label: `#${order?.orderNumber ?? ""}`, path: `/orders/${orderId}` },
  ];
  const showCancelButton = order?.status.current === SnackOrderStatus.ORDERED;
  const orderQueryKey = [
    getBuildingId(),
    "order",
    orderId,
    currUser.buildingTenantId,
    building?.supplierCompanyId,
    currUser.userId,
  ];
  const listQueryKey = [
    getBuildingId(),
    "user-orders",
    currUser.userId,
    currUser.buildingTenantId,
  ];
  const { mutate: cancelOrder, isPending: isCancelling } = useMutation({
    mutationFn: () =>
      cancelSnackOrder({
        orderId,
        userId: currUser.userId,
        userEmail: currUser.email,
      }),
    onSuccess: async ({ data: response }) => {
      const committed = response.data;
      await Promise.all([
        queryClient.cancelQueries({ queryKey: orderQueryKey }),
        queryClient.cancelQueries({ queryKey: listQueryKey }),
      ]);
      queryClient.setQueryData<SnackOrder | null>(orderQueryKey, (current) =>
        current?.id === committed.id && current.userId === currUser.userId
          ? {
              ...current,
              status: {
                ...current.status,
                current: committed.status,
                updatedAt: committed.statusUpdatedAt,
              },
            }
          : current,
      );
      // Customer lists exclude cancelled orders. Keep that committed result
      // visible even when either follow-up read fails.
      queryClient.setQueriesData<SnackOrder[]>(
        { queryKey: listQueryKey },
        (current) => current?.filter((row) => row.id !== committed.id),
      );
      addNotification(
        t("PAGES.ORDER_FOOD.NOTIFICATIONS.CANCELLED"),
        NotificationType.SUCCESS,
      );
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: orderQueryKey }),
        queryClient.invalidateQueries({ queryKey: listQueryKey }),
      ]),
    onError: (error) => {
      console.error(error);
      addNotification(t("PAGES.ORDER_FOOD.NOTIFICATIONS.CANCELLATION_FAILED"));
    },
  });

  if (readStatus === 404) return null;
  if (readStatus === 403)
    return <StatusText text={t("PAGES.MY_ORDERS.ORDER_UNAVAILABLE")} />;

  return !order ? (
    error ? (
      <OrderReadError
        retry={refetch}
        isFetching={isFetching}
        hasData={false}
        hasDraft={false}
      />
    ) : isPending ? (
      <LoadingSpinner />
    ) : null
  ) : (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--wlcm-spacing-md)",
      }}
    >
      {!!error && (
        <OrderReadError
          retry={refetch}
          isFetching={isFetching}
          hasData
          hasDraft={false}
        />
      )}
      <div>
        <div className="backAndBreadcrumbsContainer">
          <PageBackButton fallbackPath={{ to: "/orders" }} />
          <Breadcrumbs
            levels={breadcrumbs}
            onNavigate={(path) => navigate({ to: path })}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            flexWrap: "wrap",
            gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-md)",
          }}
        >
          <div className="pageHeader" style={{ marginBottom: 0 }}>
            <h1>
              {t("PAGES.MY_ORDERS.ORDER_DETAILS.HEADER", {
                orderNumber: order.orderNumber,
              })}
            </h1>
            {(supplier?.email || supplier?.phone) && (
              <p>
                <ContactDetails supplier={supplier} />
              </p>
            )}
          </div>
          {showCancelButton && (
            <Button
              danger
              icon="close"
              onClick={() => cancelOrder()}
              isLoading={isCancelling}
              disabled={isCancelling}
            >
              {t("PAGES.MY_ORDERS.ORDER_DETAILS.CANCEL")}
            </Button>
          )}
        </div>
      </div>
      <CustomerOrderStatus order={order} />
      {order.status.current !== SnackOrderStatus.COMPLETED && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--wlcm-spacing-sm)",
          }}
        >
          <StatusText
            text={t(`PAGES.MY_ORDERS.STATUS_TEXT.${order.status.current}`)}
            style={{ fontSize: "var(--wlcm-text-default)" }}
          />
          {!!order.status.notes && (
            <p
              style={{
                fontSize: "var(--wlcm-text-md)",
                marginBottom: "var(--wlcm-spacing-sm)",
              }}
            >
              "{order.status.notes}"
            </p>
          )}
        </div>
      )}
      <SnackOrderFormCheckout order={order} />
    </div>
  );
}
